import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import PortalHeader from "../components/PortalHeader";
import Button from "../components/Button";
import checkoutStyles from "./CheckoutPage.module.css";
import styles from "./CheckoutStatusPage.module.css";
import { useLanguage } from "../LanguageContext";
import usePayments from "../usePayments";

// The API re-reads the invoice from Nexus on each poll (at most every 20s)
const POLL_MS = 10 * 1000;
// Stages that won't change again on their own
const FINAL_STAGES = ["paid", "expired", "needs_review"];
// Stages the trader can still pay in, so the widget link stays up
const PAYABLE_STAGES = ["awaiting", "confirming", "partially_paid"];
// Tone per stage, for the label colour
const STAGE_TONE = {
  awaiting: "pending",
  confirming: "pending",
  partially_paid: "pending",
  paid: "done",
  expired: "failed",
  needs_review: "failed",
};

const formatUsd = (n) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// /checkout/status/<id>: where a crypto checkout waits while the trader pays in Nexus's
// widget (in another tab). Polls our API, never Nexus. `onPaid(kind)` runs once when the
// payment lands, so an activation shows as activated straight away.
export default function CheckoutStatusPage({ checkoutId, onBack, onPaid, onNavigate, navItems, activePath }) {
  const { t } = useLanguage();
  const { getCheckout } = usePayments();
  const [checkout, setCheckout] = useState(null);
  const [error, setError] = useState(null);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  // onPaid runs once per page, however often the poll restarts
  const reportedRef = useRef(false);

  useEffect(() => {
    let active = true;
    let timer;
    const poll = async () => {
      const result = await getCheckout(checkoutId);
      if (!active) return;
      if (result.error) {
        setError(result.error);
      } else {
        setError(null);
        setCheckout(result.checkout);
        if (result.checkout.status === "Paid" && !reportedRef.current) {
          reportedRef.current = true;
          onPaidRef.current(result.checkout.kind);
        }
        if (FINAL_STAGES.includes(result.checkout.cryptoStatus) || result.checkout.status === "Paid") return;
      }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [checkoutId, getCheckout]);

  const stage = checkout?.status === "Paid" ? "paid" : checkout?.cryptoStatus;
  const description = {
    awaiting: t("cs_awaiting_desc"),
    confirming: t("cs_confirming_desc"),
    partially_paid: t("cs_partially_paid_desc"),
    paid: checkout?.kind === "activation" ? t("cs_paid_activation") : t("checkout_funded_received"),
    expired: t("cs_expired_desc"),
    needs_review: t("cs_needs_review_desc"),
  }[stage];
  const tone = STAGE_TONE[stage];
  // What's left to pay: the invoice amount (Nexus's fee included) less what's arrived
  const amountDue = Math.max((checkout?.amountRequired ?? checkout?.total ?? 0) - (checkout?.amountPaid ?? 0), 0);

  return (
    <div className={checkoutStyles.page}>
      <div className={checkoutStyles.pageFrame}>
        <PortalHeader activePath={activePath} navItems={navItems} onNavigate={onNavigate} logoOnly />
        <div className={checkoutStyles.inner}>
          <div className={checkoutStyles.titleRow}>
            <button type="button" className={checkoutStyles.backBtn} onClick={onBack} aria-label="Go back">
              <ArrowLeft size={20} strokeWidth={2.5} />
            </button>
            <h1 className={checkoutStyles.title}>{t("cs_title")}</h1>
          </div>

          <div className={checkoutStyles.body}>
            <div className={checkoutStyles.formSection}>
              <div className={checkoutStyles.cryptoBox} role="status" aria-live="polite">
                {!checkout ? (
                  <p className={checkoutStyles.cryptoText}>{error ? t("cs_not_found") : t("cs_loading")}</p>
                ) : (
                  <>
                    <span className={`${styles.stage} ${styles[`stage${tone[0].toUpperCase()}${tone.slice(1)}`]} label-font`}>
                      {t(`cs_${stage}`)}
                    </span>
                    <p className={checkoutStyles.cryptoText}>{description}</p>
                    {PAYABLE_STAGES.includes(stage) && checkout.paymentUrl && (
                      <Button
                        type="button"
                        icon={<ExternalLink size={14} />}
                        onClick={() => window.open(checkout.paymentUrl, "_blank", "noopener,noreferrer")}
                      >
                        {t("cs_open_widget")}
                      </Button>
                    )}
                    {!PAYABLE_STAGES.includes(stage) && (
                      <Button type="button" onClick={onBack}>{t("checkout_back_portfolio")}</Button>
                    )}
                  </>
                )}
              </div>
              {PAYABLE_STAGES.includes(stage) && <p className={styles.warning}>{t("cs_network_warning")}</p>}
              {checkout && error && <p className={checkoutStyles.formError} role="alert">{error}</p>}
            </div>

            <div className={checkoutStyles.summarySection}>
              <h2 className={checkoutStyles.summaryTitle}>{t("checkout_summary")}</h2>
              {checkout && (
                <>
                  <div className={checkoutStyles.summaryRow}>
                    <span>{checkout.kind === "activation" ? t("checkout_activation_account") : t("checkout_account")}</span>
                    <span className={checkoutStyles.summaryValue}>{formatUsd(checkout.total)}</span>
                  </div>
                  <div className={checkoutStyles.summaryRow}>
                    <span>{t("cs_paid_so_far")}</span>
                    <span className={checkoutStyles.summaryValue}>{formatUsd(checkout.amountPaid ?? 0)}</span>
                  </div>
                  <hr className={checkoutStyles.divider} />
                  <div className={checkoutStyles.totalRow}>
                    <span>{t("cs_amount_due")}</span>
                    <span>{formatUsd(amountDue)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
